<script setup lang="ts">
import type { GetWorkspacesQuery } from './Workspace.generated'
import { WsEventType } from '~/codegen/graphql'
import { getListOfNodes } from '~/utils/relay'
import { GetDashboardsDocument, GetWorkspacesDocument } from './Workspace.generated'

const route = useRoute('teamspace-workspaceId')
const workspaceId = computed(() => route.params.workspaceId)

type Workspace = GetWorkspacesQuery['workspaces']['edges'][0]['node']
export type WorkspaceNavigationItem = ReturnType<typeof workspaceToNavigationItem>

const { watchAndExecute } = useWebsocket()

const { data, isFetching, isDone, execute: updateWorkspaces } = useQuery({
  query: GetWorkspacesDocument,
  tags: ['workspaces'],
})

const {
  data: dashboardData,
  isFetching: isFetchingDashboards,
  isDone: isDoneDashboards,
} = useQuery({
  query: GetDashboardsDocument,
  tags: ['workspace-dashboards'],
})

const events = [
  WsEventType.WorkspaceCreated,
  WsEventType.WorkspaceUpdated,
  WsEventType.WorkspaceRemoved,
]
watchAndExecute({ events, matcher: () => true, cb: () => updateWorkspaces({ cachePolicy: 'network-only' }) })

function workspaceToNavigationItem(workspace: Workspace) {
  const dashboards = dashboardData.value?.workspaces.nodes.find(ws => ws.id === workspace.id)?.dashboards.nodes ?? []

  return {
    id: workspace.id,
    label: workspace.name,
    defaultOpen: workspaceId.value === workspace.id,
    marker: workspace.marker,
    issuesTeamConfigured: workspace.issuesTeamConfigured,
    dashboards,
  }
}

const favoriteWorkspaces = computed(() => {
  return (getListOfNodes(data.value?.workspaces)
    .filter(w => !w.isGlobal)
    .filter(w => data.value?.user?.favoriteWorkspaces.find(fw => fw.id === w.id)) ?? [])
    .map(workspaceToNavigationItem)
})

const workspaces = computed(() => {
  return getListOfNodes(data.value?.workspaces)
    .filter(w => !w.isGlobal)
    .filter(w => !favoriteWorkspaces.value.map(p => p.id).includes(w.id))
    .map(workspaceToNavigationItem)
})

const isLastWorkspace = computed(() => workspaces.value.length + favoriteWorkspaces.value.length === 1)

const openFavoriteTeamspaces = ref(true)
const openAllTeamspaces = ref(false)
watch(favoriteWorkspaces, (newValue, oldValue) => {
  if (!isDone.value)
    return

  const currentWorkspaceFavoriteNow = newValue.some(w => w.id === workspaceId.value)
  const currentWorkspaceFavoriteBefore = oldValue.some(w => w.id === workspaceId.value)

  if (currentWorkspaceFavoriteNow && !currentWorkspaceFavoriteBefore)
    openFavoriteTeamspaces.value = true
  else if (!currentWorkspaceFavoriteNow || newValue.length === 0)
    openAllTeamspaces.value = true
})
</script>

<template>
  <Transition>
    <div v-show="!isFetching && !isFetchingDashboards && isDone && isDoneDashboards" class="flex min-h-0 flex-1 flex-col">
      <div class="flex flex-col gap-y-10 overflow-y-auto pb-4">
        <Details
          v-if="favoriteWorkspaces.length > 0"
          v-model="openFavoriteTeamspaces"
        >
          <template #summary>
            <div class="ml-4 py-1 text-sm font-medium text-gray-600 dark:text-gray-400">
              Favorites
            </div>
          </template>
          <NavigationItems
            :workspaces="favoriteWorkspaces"
            :favorite-workspaces="true"
            :is-last-workspace="isLastWorkspace"
          />
        </Details>
        <Details
          v-if="workspaces.length > 0"
          v-model="openAllTeamspaces"
        >
          <template #summary>
            <div class="ml-4 py-1 text-sm font-medium text-gray-600 dark:text-gray-400">
              Teamspaces
            </div>
          </template>
          <NavigationItems
            :workspaces="workspaces"
            :favorite-workspaces="false"
            :is-last-workspace="isLastWorkspace"
          />
        </Details>
      </div>

      <UDivider class="mb-2" />

      <UButton
        icon="light:plus"
        data-action="new-teamspace-button"
        data-tour="new-teamspace-button"
        color="white-nav"
        label="New teamspace"
        variant="ghost"
        size="sm"
        class="m-2 justify-start px-2 font-light text-gray-900 dark:text-white"
        :ui="{ size: { sm: '!text-nav' } }"
        :to="{ name: 'teamspace-new' }"
      />
    </div>
  </Transition>
</template>
